import Icon from "@repo/ui/components/ui/icon/icon";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverClose,
} from "@repo/ui/components/ui/popover";
import Dialog from "@repo/ui/components/ui/dialog";
import { useState } from "react";
import { Button } from "@repo/ui/components/ui/button";
import { useFormatter, useTranslations } from "next-intl";
import { cn } from "@repo/ui/lib/utils";
import { NotificationItemProps, NotificationType } from "./notifications-type";
import { Fragment } from "react";
import { useNotificationListToggle } from "./notifications-list";

function getNotificationValue<T extends keyof NotificationType>(
  key: T,
  obj: NotificationType,
) {
  return obj[key];
}

function RichTextFromApi({ html }: { html: string }) {
  const parts = html.split(/<br\s*\/?>/gi);

  return (
    <>
      {parts.map((text, i) => (
        <Fragment key={i}>
          {text}
          {/* after every chunk but the last, render a <br /> */}
          {i < parts.length - 1 && <br />}
        </Fragment>
      ))}
    </>
  );
}

export default function NotificationsItem({
  notification,
  deleteNotification,
  markAsReadNotification,
  customContent,
}: NotificationItemProps) {
  const onNotificationDelete = (id: string) => {
    deleteNotification(id);
  };

  function onNotificationMarkAsRead(id: string) {
    markAsReadNotification(id);
  }

  const notificationListToggleContext = useNotificationListToggle();
  const [actionsOpen, setActionsOpen] = useState(false);
  const format = useFormatter();
  const t = useTranslations("Notifications");

  function onNotificationClick() {
    notificationListToggleContext?.setIsToggled(true);
    if (!notification.read) {
      onNotificationMarkAsRead(notification.id);
    }
  }

  function showDialog() {
    return customContent?.key &&
      Object.hasOwn(notification, customContent?.key) &&
      notification[customContent?.key as keyof NotificationType] ? (
      <Dialog
        open={notificationListToggleContext?.isToggled || false}
        setOpen={(open) => {
          if (open) {
            onNotificationMarkAsRead(notification.id);
          }
          notificationListToggleContext?.setIsToggled(!!open);
        }}
        contentClassName="w-[90%] max-w-[1200px]"
      >
        {customContent?.element(
          getNotificationValue(
            customContent.key as keyof NotificationType,
            notification,
          )?.toString() || "",
        )}
      </Dialog>
    ) : (
      <Dialog
        header={notification.title}
        description={notification.preview}
        open={notificationListToggleContext?.isToggled || false}
        setOpen={(open) => {
          if (open) {
            onNotificationMarkAsRead(notification.id);
          }
          notificationListToggleContext?.setIsToggled(!!open);
        }}
      >
        {notification.createdAt && (
          <div className="text-primary-black leading-none rounded-[20px] border border-secondary-gray1 bg-secondary-gray3 px-4 py-2 text-[14px] w-max mb-7">
            {format.dateTime(new Date(notification.createdAt), {
              day: "numeric",
              month: "long",
            })}
          </div>
        )}
        <p className="text-primary-black max-md:text-sm text-base">
          <RichTextFromApi html={notification.message} />
        </p>
        <Button
          className="w-full mt-10 mb-1"
          onClick={() => notificationListToggleContext?.setIsToggled(false)}
        >
          {t("close")}
        </Button>
      </Dialog>
    );
  }

  return (
    <div className="w-full @container text-primary-black dark:text-primary-white [&:not(:last-child)]:border-b border-secondary-gray3 dark:border-[#D9D9D9]/20">
      <div
        className={cn(
          "grid grid-cols-[1fr_4fr_20px] @[678px]:grid-cols-[1fr_4fr_120px] gap-x-5 py-4 @[678px]:py-10 mx-0  @[678px]:mx-10",
          { "opacity-40": notification.read },
        )}
      >
        <div
          onClick={() => (!actionsOpen ? onNotificationClick() : null)}
          className="flex flex-col justify-center cursor-pointer"
        >
          <div className="flex items-center gap-3">
            <div
              className={cn(
                "h-2 w-2 bg-primary-magenta rounded-full hidden @[678px]:block",
                { invisible: notification.read },
              )}
            ></div>
            {notification.createdAt && (
              <span className="text-[24px] @[678px]:text-[40px] leading-none font-bold">
                {format.dateTime(new Date(notification.createdAt), {
                  day: "numeric",
                })}
              </span>
            )}
          </div>
          {notification.createdAt && (
            <span className="mt-0 @[678px]:mt-1 ml-0 @[678px]:ml-4 text-[12px] font-medium @[678px]:text-[16px] leading-none whitespace-normal @[678px]:whitespace-nowrap">
              {format.dateTime(new Date(notification.createdAt), {
                month: "short",
              })}{" "}
              {format.dateTime(new Date(notification.createdAt), {
                year: "numeric",
              })}
            </span>
          )}
        </div>
        <div
          onClick={() => (!actionsOpen ? onNotificationClick() : null)}
          className="cursor-pointer"
        >
          <h5 className="font-bold text-sm @[678px]:text-base">
            {notification.title}
          </h5>
          <p className="text-sm @[678px]:text-base line-clamp-3">
            {notification.preview}
          </p>
        </div>
        <div className="flex items-center justify-center">
          <Popover
            modal
            onOpenChange={(open) => setActionsOpen(open)}
            open={actionsOpen}
          >
            <PopoverTrigger onToggle={() => setActionsOpen(!actionsOpen)}>
              <Icon
                className="fill-primary-black dark:fill-primary-white w-1 @[678px]:w-5"
                icon="ThreeDots"
              />
            </PopoverTrigger>
            <PopoverContent align="end">
              <div className="divide-y px-4">
                {!notification.read && (
                  <PopoverClose asChild>
                    <div
                      role="button"
                      className="p-4 text-sm @[678px]:text-base flex items-center gap-2"
                      onClick={() => onNotificationMarkAsRead(notification.id)}
                    >
                      <Icon
                        icon="Check"
                        className="h-4 w-4 @[678px]:h-8 @[678px]:w-8 mr-2 stroke-primary-magenta"
                      />{" "}
                      {t("mark_as_read")}
                    </div>
                  </PopoverClose>
                )}
                <PopoverClose asChild>
                  <div
                    role="button"
                    className="p-4 text-sm @[678px]:text-base flex items-center gap-4"
                    onClick={() => onNotificationDelete(notification.id)}
                  >
                    <Icon
                      icon="Remove"
                      className="h-4 w-4 @[678px]:h-8 @[678px]:w-8 mr-2 stroke-primary-magenta"
                    />{" "}
                    {t("delete")}
                  </div>
                </PopoverClose>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>
      {showDialog()}
    </div>
  );
}
